import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { captureRemixErrorBoundaryError } from '@sentry/remix';

import { type OffersListIndexProps } from '@cx-modules/offers-list';

import {
	loadOffersListForClient,
	type OffersList as OffersListClient,
} from './OffersList.client';
import { OffersList as OffersListServer } from './OffersList.server';

const loadOffersList = async () => {
	let Component: typeof OffersListServer | typeof OffersListClient;
	if (typeof window !== 'undefined') {
		const client = await loadOffersListForClient();
		Component = client.Component;
	} else {
		Component = OffersListServer;
	}

	return { default: Component! };
};

const LazyOffersList = lazy(loadOffersList);

export const OffersList = (props: OffersListIndexProps) => {
	const { i18n } = useTranslation();

	return (
		<ErrorBoundary
			fallbackRender={({ error }) => {
				captureRemixErrorBoundaryError(error);
				return null;
			}}
		>
			<Suspense
				fallback={
					OffersListServer ? <OffersListServer {...props} i18n={i18n} /> : null
				}
			>
				<LazyOffersList {...props} i18n={i18n} />
			</Suspense>
		</ErrorBoundary>
	);
};
